import React, { useState, useEffect } from 'react';
import { apiClient } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Video, User } from 'lucide-react';

const NewAppointmentForm = ({ onSuccess, defaultDate }) => {
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({
    patient_id: '',
    date: defaultDate || format(new Date(), 'yyyy-MM-dd'),
    time: '',
    reason: '',
    notes: '',
    // TeleConsult fields
    appointment_type: 'in_person',
    video_provider: '',
    video_link: '',
    symptoms: '',
    symptom_duration: '',
    current_medications: '',
    patient_email: '',
    teleconsult_consent: false,
    consultation_fee: ''
  });

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await apiClient.get('/patients');
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to fetch patients');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.patient_id || !form.date || !form.time) {
      toast.error('Patient, date, and time are required');
      return;
    }

    if (form.appointment_type === 'online' && !form.teleconsult_consent) {
      toast.error('Patient consent is required for online consultation');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        consultation_fee: form.consultation_fee ? parseFloat(form.consultation_fee) : null
      };
      await apiClient.post('/appointments', payload);
      toast.success('Appointment created');
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  // Generate time slots from 7 AM to 7 PM in 30-minute intervals
  const timeSlots = [];
  for (let hour = 7; hour <= 19; hour++) {
    for (let minute of [0, 30]) {
      if (hour === 19 && minute === 30) break;
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeSlots.push(time);
    }
  }

  const isOnline = form.appointment_type === 'online';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Appointment Type Toggle */}
      <div className="flex gap-2 p-1 bg-muted rounded-lg">
        <button
          type="button"
          onClick={() => setForm({ ...form, appointment_type: 'in_person' })}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            !isOnline ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="type-in-person"
        >
          <User className="w-4 h-4" />
          In-Person
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, appointment_type: 'online' })}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            isOnline ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="type-online"
        >
          <Video className="w-4 h-4" />
          Online Consult
        </button>
      </div>

      <div>
        <Label>Patient *</Label>
        <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
          <SelectTrigger data-testid="appointment-patient-select">
            <SelectValue placeholder="Select patient" />
          </SelectTrigger>
          <SelectContent>
            {patients.map(p => (
              <SelectItem key={p.patient_id} value={p.patient_id}>
                <div className="flex items-center gap-2">
                  {p.photo_data && (
                    <img 
                      src={`data:image/png;base64,${p.photo_data}`}
                      alt=""
                      className="w-6 h-6 rounded-full"
                    />
                  )}
                  {p.full_name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Date *</Label>
          <Input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="appointment-date-input"
          />
        </div>
        
        <div>
          <Label>Time *</Label>
          <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })}>
            <SelectTrigger data-testid="appointment-time-select">
              <SelectValue placeholder="Select time" />
            </SelectTrigger>
            <SelectContent>
              {timeSlots.map(time => (
                <SelectItem key={time} value={time}>
                  {time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Online Consultation Fields */}
      {isOnline && (
        <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm font-medium text-blue-800">Online Consultation Details</p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Video Provider</Label>
              <Select value={form.video_provider} onValueChange={(v) => setForm({ ...form, video_provider: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="google_meet">Google Meet</SelectItem>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="custom">Custom Link</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Consultation Fee (₱)</Label>
              <Input
                type="number"
                value={form.consultation_fee}
                onChange={(e) => setForm({ ...form, consultation_fee: e.target.value })}
                placeholder="500"
              />
            </div>
          </div>

          <div>
            <Label>Video Link</Label>
            <Input
              value={form.video_link}
              onChange={(e) => setForm({ ...form, video_link: e.target.value })}
              placeholder="https://meet.google.com/..."
            />
          </div>

          <div>
            <Label>Symptoms / Chief Complaint</Label>
            <Textarea
              value={form.symptoms}
              onChange={(e) => setForm({ ...form, symptoms: e.target.value })}
              placeholder="Describe symptoms..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Duration of Symptoms</Label>
              <Input
                value={form.symptom_duration}
                onChange={(e) => setForm({ ...form, symptom_duration: e.target.value })}
                placeholder="e.g., 3 days"
              />
            </div>
            <div>
              <Label>Patient Email</Label>
              <Input
                type="email"
                value={form.patient_email}
                onChange={(e) => setForm({ ...form, patient_email: e.target.value })}
                placeholder="patient@email.com"
              />
            </div>
          </div>

          <div>
            <Label>Current Medications</Label>
            <Input
              value={form.current_medications}
              onChange={(e) => setForm({ ...form, current_medications: e.target.value })}
              placeholder="List any current medications"
            />
          </div>

          <div className="flex items-start gap-3 p-3 bg-white rounded border">
            <Checkbox
              id="consent"
              checked={form.teleconsult_consent}
              onCheckedChange={(checked) => setForm({ ...form, teleconsult_consent: checked })}
            />
            <label htmlFor="consent" className="text-sm leading-tight cursor-pointer">
              <span className="font-medium">Patient Consent *</span>
              <p className="text-muted-foreground mt-1">
                Patient consents to receive medical consultation through online communication.
              </p>
            </label>
          </div>
        </div>
      )}

      {/* In-Person Fields */}
      {!isOnline && (
        <>
          <div>
            <Label>Reason for Visit</Label>
            <Input
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g., Follow-up, Check-up, Consultation"
            />
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any additional notes..."
              rows={2}
            />
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" disabled={loading} data-testid="submit-appointment-btn">
          {loading ? 'Creating...' : isOnline ? 'Book Online Consultation' : 'Create Appointment'}
        </Button>
      </div>
    </form>
  );
};

export default NewAppointmentForm;
